import { createSelector } from 'reselect';
import { IAppState } from 'spw-redux/reducer';
import { formatAddress } from 'spw-components/address-autocomplete/utils';
import { BenchmarkProject, MissingData, UserRoleInReport } from './types';

export const selectBenchmarkProjects = (state: IAppState) =>
  state.benchmarkReportsReducer.projects;
export const selectIsLoadingBenchmarkProjects = (state: IAppState) =>
  state.benchmarkReportsReducer.isLoadingProjects;
export const selectSelectedBenchmarkProjects = (state: IAppState) =>
  state.benchmarkReportsReducer.selectedProjects;
export const selectSelectedBenchmarkProjectsMap = createSelector(
  selectSelectedBenchmarkProjects,
  (selectedProjects): Record<number, BenchmarkProject> => {
    return selectedProjects.reduce((map, benchmarkProject) => {
      return {
        ...map,
        [benchmarkProject.project.id]: benchmarkProject,
      };
    }, {});
  }
);

export const selectBenchmarkReportLinkedProjectsCount = createSelector(
  selectSelectedBenchmarkProjects,
  (selectedProjects) =>
    selectedProjects?.reduce((count, brp) => count + (brp.linked ? 1 : 0), 0)
);

const projectLocations = (state: IAppState) =>
  state.benchmarkReportsReducer.projectsLocations;

export const selectBenchmarkProjectsLocations = createSelector(
  projectLocations,
  (locations) => {
    return locations.map((location) => ({
      ...location,
      id: formatAddress(location),
    }));
  }
);

export const selectBenchmarkProjectsUSFRange = (state: IAppState) =>
  state.benchmarkReportsReducer.projectsUSFRange;

export const selectBenchmarkReportSummary = (state: IAppState) =>
  state.benchmarkReportsReducer.summary;

export const selectBenchmarkReportSummaryIsLoading = (state: IAppState) =>
  state.benchmarkReportsReducer.summaryIsLoading;

export const selectClientsOfBenchmarkProjects = (state: IAppState) =>
  state.benchmarkReportsReducer.clients;

export const selectCurrentBechmarkReport = (state: IAppState) =>
  state.benchmarkReportsReducer.currentReport;

export const selectCurrentBechmarkReportIsLoading = (state: IAppState) =>
  state.benchmarkReportsReducer.currentReportIsLoading;

export const selectCurrentBenchmarkReportCategoriesMap = (state: IAppState) =>
  state.benchmarkReportsReducer.currentReportCategoriesMap;

export const selectCurrentBenchmarkReportIconsMap = (state: IAppState) =>
  state.benchmarkReportsReducer.currentReportIconsMap;

export const selectCartHasAtLeastOneValidProject = createSelector(
  selectSelectedBenchmarkProjects,
  (selectedProjects) => {
    return (
      selectedProjects.length === 0 ||
      selectedProjects.some((sp) => sp.linked && !sp.forciblyUnlinked)
    );
  }
);

export const selectJustVerifiedBenchmarkReportId = (state: IAppState) =>
  state.benchmarkReportsReducer.highlightedReportId;

export const selectBenchmarkReports = (state: IAppState) =>
  state.benchmarkReportsReducer.reportsList.items;

export const selectTotalBenchmarkReportsCount = (state: IAppState) =>
  state.benchmarkReportsReducer.reportsList.total;

export const selectBenchmarkReportsAreLoading = (state: IAppState) =>
  state.benchmarkReportsReducer.reportsList.loading;

export const selectBenchmarkReportEditors = (state: IAppState) =>
  state.benchmarkReportsReducer.editors;

export const selectMissingProjectData = (state: IAppState) =>
  state.benchmarkReportsReducer.missingProjectData;

export const selectMissingData = (state: IAppState) =>
  state.benchmarkReportsReducer.missingData;

export const selectUserIsViewOnlyForReport = (state: IAppState) =>
  !state.benchmarkReportsReducer.currentReport ||
  state.benchmarkReportsReducer.currentReport?.currentUserRole ===
    UserRoleInReport.viewer;

const filterMissingData = (missingData: MissingData, isResolved: boolean) => {
  if (!missingData) return null;

  const resolvedMissingData = {
    ...missingData,
    missingFields: missingData.missingFields.filter(
      (missingField) => missingField.isResolved === isResolved
    ),
    missingBuildingData: missingData.missingBuildingData
      .map((missingBuildingData) => {
        return {
          ...missingBuildingData,
          missingFloorData: missingBuildingData.missingFloorData
            .map((missingFloorData) => {
              return {
                ...missingFloorData,
                missingFields: missingFloorData.missingFields.filter(
                  (missingField) => missingField.isResolved === isResolved
                ),
              };
            })
            .filter(
              (missingFloorData) => missingFloorData.missingFields.length > 0
            ),
        };
      })
      .filter(
        (missingBuildingData) => missingBuildingData.missingFloorData.length > 0
      ),
  };

  if (
    resolvedMissingData.missingFields.length ||
    resolvedMissingData.missingBuildingData.length
  ) {
    return resolvedMissingData;
  }
  return null;
};

export const selectUnresolvedMissingData = createSelector(
  selectMissingData,
  (missingData) => {
    return filterMissingData(missingData, false);
  }
);

export const selectResolvedMissingData = createSelector(
  selectMissingData,
  (missingData) => {
    return filterMissingData(missingData, true);
  }
);

export const selectMissingDataByFloor = createSelector(
  selectMissingData,
  (missingData) => {
    return (
      missingData?.missingBuildingData?.reduce(
        (missingBuildingDataMap, missingBuildingData) => {
          return {
            ...missingBuildingDataMap,
            ...missingBuildingData.missingFloorData.reduce(
              (missingFloorDataMap, missingFloorData) => {
                return {
                  ...missingFloorDataMap,
                  [missingFloorData.floorId]: missingFloorData.missingFields.filter(
                    (missingField) => !missingField.isResolved
                  ),
                };
              },
              {}
            ),
          };
        },
        {}
      ) || {}
    );
  }
);
